'use client';

/**
 * RelatedMediaPanel — master-detail media panel for a RAG turn with
 * MULTIPLE references (owner mockup "Related Art of Living videos";
 * design 4a annotation: "the master-detail for multiple videos").
 *
 * Left: selectable rows (play/doc icon tile, title, duration or "Article").
 * Right: the selected reference featured large — video poster that swaps to
 * an inline YouTube iframe on play, or the article preview linking out —
 * with Lora title, description and source row beneath.
 *
 * Mixed content rides the same layout: articles get a doc icon row and a
 * link-out featured pane. A single reference uses MediaHeroCard instead.
 */

import React, { memo, useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import type { RAGReference } from '@/types/rag';
import { cn } from '@/lib/utils';
import {
  getYouTubeId,
  youTubeThumbnailUrl,
  youTubeEmbedUrl,
} from '@/lib/youtube';
import { useYouTubeDuration } from '@/hooks/useYouTubeDuration';
import { CountPill } from '@/components/assistant/primitives/count-pill';
import { defaultPreview } from './default-preview';
import {
  PlayButton,
  DurationBadge,
  YouTubeChip,
  hostInfo,
} from './media-hero-card';

interface RelatedMediaPanelProps {
  references: RAGReference[];
  className?: string;
}

export const RelatedMediaPanel = memo(function RelatedMediaPanel({
  references,
  className,
}: RelatedMediaPanelProps) {
  // Score order throughout: both the list AND the initial featured item are
  // the top-scored reference (index 0), regardless of media type — the most
  // relevant result always leads, and the featured item can never be a middle
  // row. (allVideos is only used for the header label below.)
  const sorted = useMemo(
    () => [...references].sort((a, b) => (b.score ?? 0) - (a.score ?? 0)),
    [references]
  );
  const allVideos = useMemo(
    () => sorted.every((r) => getYouTubeId(r.url) !== null),
    [sorted]
  );
  const [selectedIdx, setSelectedIdx] = useState(0);
  // Playing state lives here (not in the featured pane) so switching rows
  // always stops the previous video.
  const [playing, setPlaying] = useState(false);

  if (sorted.length === 0) return null;
  const selected = sorted[Math.min(selectedIdx, sorted.length - 1)];

  const handleSelect = (idx: number) => {
    setSelectedIdx(idx);
    setPlaying(false);
  };

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-[16px] rounded-[20px] border-[1px] border-[#f0e7da] bg-white px-[20px] py-[18px]',
        'shadow-[0_1px_4px_rgba(0,0,0,0.04)]',
        "font-['Work_Sans']",
        className
      )}
    >
      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="min-w-0 truncate text-[15.5px] font-semibold leading-snug text-[#31364e]">
          Related Art of Living {allVideos ? 'videos' : 'resources'}
        </h3>
        <CountPill
          count={sorted.length}
          noun={allVideos ? 'videos' : 'sources'}
        />
      </div>

      {/* ── Master-detail body (stacks featured-first below sm) ── */}
      <div className="flex flex-col-reverse gap-[16px] sm:grid sm:grid-cols-[minmax(190px,236px)_1fr] sm:gap-[20px]">
        <div className="flex flex-col gap-[6px] sm:border-r-[1px] sm:border-[#f0e7da] sm:pr-[16px]">
          {sorted.map((r, i) => (
            <MediaRow
              key={`${r.url}-${i}`}
              refItem={r}
              selected={i === selectedIdx}
              onSelect={() => handleSelect(i)}
            />
          ))}
        </div>
        <FeaturedPane
          refItem={selected}
          playing={playing}
          onPlay={() => setPlaying(true)}
        />
      </div>
    </div>
  );
});

// ── List row ───────────────────────────────────────────────────────

function MediaRow({
  refItem,
  selected,
  onSelect,
}: {
  refItem: RAGReference;
  selected: boolean;
  onSelect: () => void;
}): React.ReactElement {
  const isVideo = getYouTubeId(refItem.url) !== null;
  const isShort = isVideo && /\/shorts\//.test(refItem.url || '');
  const duration = useYouTubeDuration(isVideo ? refItem.url : null);

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'flex w-full items-center gap-[10px] rounded-[14px] border-[1px] px-[10px] py-[9px] text-left transition-colors',
        selected
          ? 'border-[#E8C7A0] bg-[#FFF6EC]'
          : 'border-transparent hover:bg-[#FBF7F1]'
      )}
    >
      <span className="flex size-[38px] shrink-0 items-center justify-center rounded-[9px] bg-[#EDE4D6]">
        {isVideo ? (
          <svg
            aria-hidden
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="#8a7c68"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        ) : (
          <FileText aria-hidden className="size-[15px] text-[#8a7c68]" />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="line-clamp-1 text-[14px] font-semibold text-[#31364e]">
          {refItem.title}
        </span>
        <span className="text-[12.5px] text-[#8a7c68]">
          {isShort
            ? 'Short Video'
            : isVideo
              ? (duration ?? 'Video')
              : 'Article'}
        </span>
      </span>
    </button>
  );
}

// ── Featured pane ──────────────────────────────────────────────────

function FeaturedPane({
  refItem,
  playing,
  onPlay,
}: {
  refItem: RAGReference;
  playing: boolean;
  onPlay: () => void;
}): React.ReactElement {
  const youtubeId = getYouTubeId(refItem.url);
  // Shorts are vertical (9:16): show them in a portrait box like YouTube Shorts,
  // not the 16:9 landscape frame used for regular videos/articles.
  const isShort = !!youtubeId && /\/shorts\//.test(refItem.url || '');
  const mediaAspect = isShort
    ? 'aspect-[9/16] mx-auto w-full max-w-[300px]'
    : 'aspect-video w-full';
  const duration = useYouTubeDuration(youtubeId ? refItem.url : null);
  const preview = useMemo(() => defaultPreview(refItem), [refItem]);
  const host = useMemo(() => hostInfo(refItem.url), [refItem.url]);

  return (
    <div className="flex min-w-0 flex-col gap-[10px]">
      {/* Media area */}
      {youtubeId && playing ? (
        <div
          className={cn(mediaAspect, 'overflow-hidden rounded-[12px] bg-black')}
        >
          <iframe
            src={youTubeEmbedUrl(youtubeId, { autoplay: true })}
            title={refItem.title || 'Video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="size-full border-0"
          />
        </div>
      ) : youtubeId ? (
        <button
          type="button"
          onClick={onPlay}
          aria-label={refItem.title ? `Play ${refItem.title}` : 'Play video'}
          className={cn(
            'group relative block cursor-pointer overflow-hidden rounded-[12px] bg-black p-0',
            mediaAspect
          )}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={youTubeThumbnailUrl(youtubeId)}
            alt=""
            className="absolute inset-0 size-full object-cover"
            onError={(e) => {
              const img = e.currentTarget;
              if (img.dataset.fallback) return;
              img.dataset.fallback = '1';
              img.src = preview.src;
            }}
          />
          <PlayButton />
          {duration && (
            <DurationBadge duration={duration} position="bottom-right" />
          )}
        </button>
      ) : (
        <a
          href={refItem.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={refItem.title ? `Read ${refItem.title}` : 'Read article'}
          className="relative block aspect-video w-full overflow-hidden rounded-[12px] no-underline"
          style={
            preview.category === 'wisdom'
              ? {
                  background:
                    'radial-gradient(circle at 50% 30%, #fff3df 0%, #f6d9a6 68%, #eec988 100%)',
                }
              : { background: '#000' }
          }
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={preview.src}
            alt=""
            className={cn(
              'absolute inset-0 size-full',
              preview.fit === 'cover'
                ? 'object-cover'
                : 'object-contain object-bottom'
            )}
          />
        </a>
      )}

      {/* Title, description, source */}
      <div className="flex flex-col gap-[6px]">
        <div className="font-[family-name:var(--font-lora)] text-[19px] font-semibold leading-[1.3] text-[#31364e]">
          {refItem.title}
        </div>
        {refItem.description && (
          <div className="line-clamp-3 text-[14px] leading-[1.55] text-[#6b7184]">
            {refItem.description}
          </div>
        )}
        <div className="mt-0.5 flex items-center gap-[8px]">
          {youtubeId ? (
            <>
              <YouTubeChip />
              <span className="text-[13.5px] font-medium text-[#8a7c68]">
                Art of Living · YouTube
              </span>
            </>
          ) : (
            <>
              {host.favicon && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={host.favicon}
                  alt=""
                  className="size-[15px] rounded-[3px] object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
              <a
                href={refItem.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[13.5px] font-medium text-[#8a7c68] underline-offset-2 hover:underline"
              >
                {host.domain}
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default RelatedMediaPanel;
